import { useState } from 'react';
import { useT, type MessageKey } from '../../i18n/i18n';
import { accountStore, sendDeviceCode, verifyDeviceCode } from '../../state/accountStore';
import { useStore } from '../../state/createStore';
import { sessionStore, signOutAction } from '../../state/sessionStore';
import { Button, Card, Field, InlineMessage, SectionHeader, TextInput } from '../../ui/components';

// New device (Owner decision list 2026-10-19 item 50, accountProtocol.ts L-5 / L-7): a device this account
// has not used before asks for a code sent to the account's e-mail before anything else opens. Verifying
// it removes the device used longest ago when two are already signed in. Sign out is always offered.
// Every message sits under what it is about (Owner rule 9): the sending answer under the send button,
// the code's answer under the code.
export function DeviceVerifyScreen() {
  const { t } = useT();
  const email = useStore(sessionStore, (s) => s.email);
  const busy = useStore(accountStore, (s) => s.busy);
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [sendMessage, setSendMessage] = useState<{ kind: 'ok' | 'err'; key: MessageKey } | null>(null);
  const [codeMessage, setCodeMessage] = useState<MessageKey | null>(null);

  async function send() {
    if (!email) return;
    setSendMessage(null);
    const outcome = await sendDeviceCode(email);
    if (outcome === 'sent') setSent(true);
    setSendMessage(outcome === 'sent' ? { kind: 'ok', key: 'device.sent' } : { kind: 'err', key: outcome === 'rateLimited' ? 'device.rateLimited' : 'device.sendFailed' });
  }

  async function verify() {
    if (!email) return;
    setCodeMessage(null);
    const outcome = await verifyDeviceCode(email, code);
    if (outcome === 'invalid' || outcome === 'stillRequired') setCodeMessage('device.invalid');
    else if (outcome === 'failed') setCodeMessage('device.verifyFailed');
  }

  const codeOk = /^\d{6,10}$/.test(code.trim());

  return (
    <div className="screen device-verify" data-testid="device-verify-screen">
      <div className="screen-body">
        <Card testId="device-verify-card">
          <SectionHeader title={t('device.title')} />
          <p className="card-body">{email ? t('device.body', { email }) : t('device.bodyNoEmail')}</p>
          <div className="form-stack">
            <Button variant={sent ? 'secondary' : 'primary'} icon="mail" disabled={busy !== null || !email} onClick={() => void send()} testId="device-send-code">
              {sent ? t('device.resend') : t('device.send')}
            </Button>
            {sendMessage ? <InlineMessage kind={sendMessage.kind} text={t(sendMessage.key)} /> : null}
            <Field label={t('device.codeLabel')} htmlFor="device-code">
              <TextInput id="device-code" value={code} onChange={(value) => setCode(value.replace(/\s+/g, ''))} inputMode="numeric" autoComplete="one-time-code" testId="device-code" />
            </Field>
            {codeMessage ? <InlineMessage kind="err" text={t(codeMessage)} /> : null}
            <Button disabled={!codeOk || busy !== null} onClick={() => void verify()} testId="device-verify">
              {t('device.verify')}
            </Button>
          </div>
          <p className="card-note">{t('device.limitNote')}</p>
          <div className="card-actions">
            <Button variant="text" icon="logout" onClick={() => void signOutAction()} testId="device-sign-out">
              {t('device.signOut')}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
